import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL, getBatchReport, getCandidateFileUrl, downloadCandidateFile, bulkDownloadCandidates, updateCandidateStatus, previewEmail, sendEmail, startEvaluation } from '../api/api';

const UNKNOWN_NAME = 'Unknown';

const EMAIL_TEMPLATE_TYPE_INVITATION = 'invitation';
const EMAIL_TEMPLATE_TYPE_REJECTION = 'rejection';
const EMAIL_TEMPLATE_TYPE_SECOND_INTERVIEW = 'second_interview';

const EMAIL_TEMPLATE_OPTIONS = [
  { value: EMAIL_TEMPLATE_TYPE_INVITATION, label: 'Interview Invitation' },
  { value: EMAIL_TEMPLATE_TYPE_REJECTION, label: 'Rejection' },
  { value: EMAIL_TEMPLATE_TYPE_SECOND_INTERVIEW, label: 'Second Interview Invitation' },
];

const EMAIL_SUCCESS_MESSAGES = {
  [EMAIL_TEMPLATE_TYPE_INVITATION]: 'Interview invitation sent successfully!',
  [EMAIL_TEMPLATE_TYPE_REJECTION]: 'Rejection email sent successfully!',
  [EMAIL_TEMPLATE_TYPE_SECOND_INTERVIEW]: 'Second interview invitation sent successfully!',
};

const formatDisplayName = (name) => {
  if (!name || typeof name !== 'string') {
    return UNKNOWN_NAME;
  }
  const cleaned = name.replace(/\s+/g, ' ').trim();
  return cleaned || UNKNOWN_NAME;
};

const getNameInitials = (name) => {
  const formatted = formatDisplayName(name);
  if (formatted === UNKNOWN_NAME) {
    return 'UN';
  }
  const initials = formatted
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
  return initials || 'UN';
};

const ReportPage = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedCandidateId, setExpandedCandidateId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'total_score', direction: 'descending' });
  const [showCvModal, setShowCvModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [exportLoading, setExportLoading] = useState(null);
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
  const [reassessLoading, setReassessLoading] = useState(false);
  const [statusModalCandidate, setStatusModalCandidate] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: '', notes: '' });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Email modal states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalCandidate, setEmailModalCandidate] = useState(null);
  const [emailForm, setEmailForm] = useState({ recipient_email: '', subject: '', body: '' });
  const [emailTemplateType, setEmailTemplateType] = useState(EMAIL_TEMPLATE_TYPE_INVITATION);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailTemplateLoading, setEmailTemplateLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Status options
  const statusOptions = [
    'Under Review',
    'Shortlisted',
    'Rejected',
    'Interview Scheduled',
    'Interview Invitation Sent',
    'Interviewed',
    'Second Interview',
    'Offer Extended',
    'Hired',
    'Declined',
    'On Hold'
  ];

  // Fetch report data
  useEffect(() => {
    const fetchReport = async () => {
      try {
        const data = await getBatchReport(batchId);
        setReport(data);
      } catch (err) {
        setError(err.detail || 'Error fetching evaluation report.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [batchId]);

  // Handle sorting
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted candidates
  const sortedCandidates = useMemo(() => {
    if (!report || !report.candidates) return [];
    
    const sortableItems = [...report.candidates];
    sortableItems.sort((a, b) => {
      // Handle null values
      if (a[sortConfig.key] === null) return 1;
      if (b[sortConfig.key] === null) return -1;
      
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    
    return sortableItems;
  }, [report, sortConfig]);

  // Get criteria names
  const criteriaNames = useMemo(() => {
    if (!report || !report.scoring_criteria) return [];
    return Object.keys(report.scoring_criteria);
  }, [report]);

  // Toggle candidate details
  const toggleDetails = (candidateId) => {
    if (expandedCandidateId === candidateId) {
      setExpandedCandidateId(null);
    } else {
      setExpandedCandidateId(candidateId);
    }
  };

  // Handle export with loading state and error handling
  const handleExport = async (format) => {
    setExportLoading(format);
    
    try {
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = `${API_URL}/reports/export/${batchId}/${format}`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Add timestamp to prevent caching issues
      link.href += `?t=${Date.now()}`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success message
      setTimeout(() => {
        // You could add a toast notification here
        console.log(`${format.toUpperCase()} export initiated successfully`);
      }, 500);
      
    } catch (error) {
      console.error('Export failed:', error);
      setError(`Failed to export ${format.toUpperCase()} file. Please try again.`);
    } finally {
      setExportLoading(null);
    }
  };

  // Format score as percentage
  const formatScore = (score) => {
    return score !== null ? `${Math.round(score * 100)}%` : 'N/A';
  };

  // Handle CV modal
  const openCvModal = (candidate) => {
    setSelectedCandidate(candidate);
    setShowCvModal(true);
  };

  const closeCvModal = () => {
    setShowCvModal(false);
    setSelectedCandidate(null);
  };

  // Handle keyboard events for modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showCvModal) {
        closeCvModal();
      }
    };

    if (showCvModal) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showCvModal]);

  // Handle candidate selection
  const toggleCandidateSelection = (candidateId) => {
    const newSelected = new Set(selectedCandidates);
    if (newSelected.has(candidateId)) {
      newSelected.delete(candidateId);
    } else {
      newSelected.add(candidateId);
    }
    setSelectedCandidates(newSelected);
  };

  // Select all candidates
  const selectAllCandidates = () => {
    if (selectedCandidates.size === sortedCandidates.length) {
      setSelectedCandidates(new Set());
    } else {
      setSelectedCandidates(new Set(sortedCandidates.map(c => c.id)));
    }
  };

  // Handle bulk CV download
  const handleBulkDownload = async () => {
    if (selectedCandidates.size === 0) {
      setError('Please select at least one candidate to download CVs.');
      return;
    }

    setBulkDownloadLoading(true);
    try {
      const candidateIds = Array.from(selectedCandidates);
      const result = await bulkDownloadCandidates(candidateIds);
      
      // Show success message
      setTimeout(() => {
        console.log(`Successfully downloaded ${candidateIds.length} CV files as ${result.filename}`);
      }, 500);

      // Clear selection after successful download
      setSelectedCandidates(new Set());
      
    } catch (error) {
      console.error('Bulk download failed:', error);
      setError(`Failed to download CV files: ${error.message}`);
    } finally {
      setBulkDownloadLoading(false);
    }
  };

  // Handle individual CV download
  const handleSingleDownload = async (candidateId) => {
    try {
      await downloadCandidateFile(candidateId);
    } catch (downloadError) {
      console.error('Download failed:', downloadError);
      setError('Failed to download CV file. Please try again.');
    }
  };

  const handleReassessment = async () => {
    const confirmed = window.confirm(
      'Re-run assessment for all candidates in this batch? Existing scores will be replaced when the new evaluation completes.'
    );
    if (!confirmed) {
      return;
    }

    try {
      setReassessLoading(true);
      setError(null);
      await startEvaluation(batchId, true);
      navigate(`/evaluation/${batchId}`, { state: { skipAutoStart: true } });
    } catch (reassessError) {
      console.error('Re-assessment failed:', reassessError);
      const errorMessage = reassessError.detail || reassessError.message || 'Failed to start re-assessment.';
      setError(errorMessage);
    } finally {
      setReassessLoading(false);
    }
  };

  // Handle status modal
  const openStatusModal = (candidate) => {
    setStatusModalCandidate(candidate);
    setStatusForm({
      status: candidate.status || 'Under Review',
      notes: candidate.notes || ''
    });
    setShowStatusModal(true);
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setStatusModalCandidate(null);
    setStatusForm({ status: '', notes: '' });
  };

  // Handle status update
  const handleStatusUpdate = async () => {
    if (!statusModalCandidate) return;

    try {
      setUpdatingStatus(true);
      await updateCandidateStatus(statusModalCandidate.id, statusForm);
      
      // Update the local state
      setReport(prevReport => ({
        ...prevReport,
        candidates: prevReport.candidates.map(candidate => 
          candidate.id === statusModalCandidate.id 
            ? { 
                ...candidate, 
                status: statusForm.status, 
                notes: statusForm.notes,
                status_updated_at: new Date().toISOString()
              }
            : candidate
        )
      }));

      closeStatusModal();
    } catch (error) {
      console.error('Status update failed:', error);
      setError(`Failed to update candidate status: ${error.message}`);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    const statusColors = {
      'Under Review': 'bg-blue-100 text-blue-800',
      'Shortlisted': 'bg-green-100 text-green-800', 
      'Rejected': 'bg-red-100 text-red-800',
      'Interviewed': 'bg-purple-100 text-purple-800',
      'Interview Scheduled': 'bg-purple-100 text-purple-800',
      'Interview Invitation Sent': 'bg-purple-100 text-purple-800',
      'Second Interview': 'bg-purple-100 text-purple-800',
      'Offer Extended': 'bg-yellow-100 text-yellow-800',
      'Hired': 'bg-green-100 text-green-800',
      'Declined': 'bg-gray-100 text-gray-800',
      'On Hold': 'bg-orange-100 text-orange-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  // Handle email modal
  const loadEmailPreview = async (candidate, templateType, preserveRecipientEmail = false) => {
    const preview = await previewEmail(candidate.id, candidate.email, templateType);

    setEmailForm((previousForm) => ({
      recipient_email: preserveRecipientEmail
        ? previousForm.recipient_email || preview.recipient_email
        : preview.recipient_email,
      subject: preview.subject,
      body: preview.body,
    }));
  };

  const openEmailModal = async (candidate) => {
    try {
      setEmailLoading(true);
      setEmailModalCandidate(candidate);
      setEmailTemplateType(EMAIL_TEMPLATE_TYPE_INVITATION);
      await loadEmailPreview(candidate, EMAIL_TEMPLATE_TYPE_INVITATION);
      setShowEmailModal(true);
    } catch (previewError) {
      console.error('Failed to load email template:', previewError);
      setError(`Failed to load email template: ${previewError.message}`);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailTemplateChange = async (event) => {
    const selectedTemplateType = event.target.value;
    if (!emailModalCandidate || selectedTemplateType === emailTemplateType) {
      return;
    }

    try {
      setEmailTemplateLoading(true);
      setEmailTemplateType(selectedTemplateType);
      await loadEmailPreview(emailModalCandidate, selectedTemplateType, true);
    } catch (previewError) {
      console.error('Failed to load email template:', previewError);
      setError(`Failed to load email template: ${previewError.message}`);
    } finally {
      setEmailTemplateLoading(false);
    }
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setEmailModalCandidate(null);
    setEmailTemplateType(EMAIL_TEMPLATE_TYPE_INVITATION);
    setEmailForm({ recipient_email: '', subject: '', body: '' });
  };

  // Handle email sending
  const handleSendEmail = async () => {
    if (!emailModalCandidate) return;

    try {
      setSendingEmail(true);
      
      await sendEmail(
        emailModalCandidate.id,
        emailForm.recipient_email,
        emailForm.subject,
        emailForm.body
      );
      
      setSuccess(EMAIL_SUCCESS_MESSAGES[emailTemplateType] || 'Email sent successfully!');
      setTimeout(() => setSuccess(null), 3000);
      closeEmailModal();
      
    } catch (error) {
      console.error('Email sending failed:', error);
      setError(`Failed to send email: ${error.message}`);
    } finally {
      setSendingEmail(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-10 flex justify-center">
        <svg
          className="animate-spin h-8 w-8 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <span className="ml-3 text-gray-600">Loading evaluation report...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full px-4 py-10">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="w-full px-4 py-10">
        <div className="bg-gray-100 border border-gray-300 text-gray-700 px-4 py-3 rounded">
          No report data available.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none">
      {/* Success message */}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
          {success}
        </div>
      )}
      
      <div className="bg-white shadow-md rounded-lg w-full mb-8 overflow-hidden">
        <div className="p-6 pb-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">CV Evaluation Results</h1>
            <p className="text-gray-600 mt-1">Job Title: {report.job_title}</p>
            {report.job_requirements && (
              <div className="mt-2 max-w-2xl">
                <h3 className="text-sm font-medium text-gray-700">Job Requirements:</h3>
                <p className="text-sm text-gray-600 mt-1">{report.job_requirements}</p>
              </div>
            )}
          </div>
          <div className="flex flex-col space-y-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleReassessment}
                disabled={reassessLoading || exportLoading !== null}
                data-testid="report-reassess-button"
                className="bg-indigo-600 text-white py-2 px-4 rounded hover:bg-indigo-700 focus:outline-none flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reassessLoading ? (
                  <svg className="animate-spin h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                )}
                {reassessLoading ? 'Starting...' : 'Re-assess'}
              </button>
            </div>
            {/* Export buttons row */}
            <div className="flex space-x-2">
              <button
                onClick={() => handleExport('csv')}
                disabled={exportLoading !== null}
                className="bg-gray-200 text-gray-700 py-2 px-4 rounded hover:bg-gray-300 focus:outline-none flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading === 'csv' ? (
                  <svg className="animate-spin h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                )}
                {exportLoading === 'csv' ? 'Exporting...' : 'CSV'}
              </button>
              <button
                onClick={() => handleExport('excel')}
                disabled={exportLoading !== null}
                className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 focus:outline-none flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading === 'excel' ? (
                  <svg className="animate-spin h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                )}
                {exportLoading === 'excel' ? 'Exporting...' : 'Excel'}
              </button>
              <button
                onClick={() => handleExport('pdf')}
                disabled={exportLoading !== null}
                className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 focus:outline-none flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading === 'pdf' ? (
                  <svg className="animate-spin h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                )}
                {exportLoading === 'pdf' ? 'Exporting...' : 'PDF'}
              </button>
            </div>
            
            {/* Bulk download controls */}
            {selectedCandidates.size > 0 && (
              <div className="flex items-center space-x-2 bg-blue-50 px-3 py-2 rounded border">
                <span className="text-sm text-blue-700">
                  {selectedCandidates.size} candidate{selectedCandidates.size > 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={handleBulkDownload}
                  disabled={bulkDownloadLoading}
                  className="bg-blue-600 text-white py-1 px-3 rounded text-sm hover:bg-blue-700 focus:outline-none flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkDownloadLoading ? (
                    <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  )}
                  {bulkDownloadLoading ? 'Downloading...' : 'Download CVs'}
                </button>
                <button
                  onClick={() => setSelectedCandidates(new Set())}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Candidates table — full width with horizontal scroll when needed */}
        <div className="w-full overflow-x-auto border-t border-gray-200">
          <table className="w-full min-w-[1200px] divide-y divide-gray-200 table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="w-10 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={sortedCandidates.length > 0 && selectedCandidates.size === sortedCandidates.length}
                    onChange={selectAllCandidates}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </th>
                <th
                  scope="col"
                  className="min-w-[180px] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Candidate
                </th>
                <th
                  scope="col"
                  className="min-w-[160px] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Email
                </th>
                <th
                  scope="col"
                  className="min-w-[100px] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => requestSort('total_score')}
                >
                  <div className="flex items-center">
                    Total Score
                    {sortConfig.key === 'total_score' && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-4 w-4 ml-1 ${
                          sortConfig.direction === 'ascending' ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </th>
                <th
                  scope="col"
                  className="min-w-[110px] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => requestSort('status')}
                >
                  <div className="flex items-center">
                    Status
                    {sortConfig.key === 'status' && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-4 w-4 ml-1 ${
                          sortConfig.direction === 'ascending' ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </th>
                {criteriaNames.map((criterion) => (
                  <th
                    key={criterion}
                    scope="col"
                    className="min-w-[120px] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort(criterion)}
                  >
                    <div className="flex items-center">
                      {criterion.replace(/_/g, ' ')}
                      {sortConfig.key === criterion && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-4 w-4 ml-1 ${
                            sortConfig.direction === 'ascending' ? 'transform rotate-180' : ''
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </th>
                ))}
                <th
                  scope="col"
                  className="sticky right-0 z-10 min-w-[300px] bg-gray-50 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedCandidates.map((candidate) => (
                <React.Fragment key={candidate.id}>
                  <tr className={`group hover:bg-gray-50 ${expandedCandidateId === candidate.id ? 'bg-gray-50' : ''}`}>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedCandidates.has(candidate.id)}
                        onChange={() => toggleCandidateSelection(candidate.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex items-center min-w-[160px]">
                        <div className="flex-shrink-0 h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-800 font-medium text-xs">
                            {getNameInitials(candidate.name)}
                          </span>
                        </div>
                        <div className="ml-3 min-w-0">
                          <div className="text-sm font-medium text-gray-900">{formatDisplayName(candidate.name)}</div>
                          <div className="text-xs text-gray-500 break-all">{candidate.file_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="text-sm text-gray-900 break-all">{candidate.email || 'Not provided'}</div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap align-top">
                      <div className={`text-sm font-medium ${
                        candidate.total_score >= 0.7 
                          ? 'text-green-600' 
                          : candidate.total_score >= 0.4 
                            ? 'text-yellow-600' 
                            : 'text-red-600'
                      }`}>
                        {formatScore(candidate.total_score)}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className={`text-sm font-medium ${getStatusBadgeColor(candidate.status)}`}>
                        {candidate.status}
                      </div>
                    </td>
                    {criteriaNames.map((criterion) => (
                      <td key={criterion} className="px-3 py-3 whitespace-nowrap align-top">
                        <div className="text-sm text-gray-900">{formatScore(candidate[criterion])}</div>
                      </td>
                    ))}
                    <td className={`sticky right-0 z-10 px-3 py-3 text-sm font-medium align-top min-w-[300px] shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50 ${expandedCandidateId === candidate.id ? 'bg-gray-50' : 'bg-white'}`}>
                      <div className="flex flex-wrap gap-x-2 gap-y-1">
                        <button
                          onClick={() => toggleDetails(candidate.id)}
                          className="text-primary-600 hover:text-primary-900 flex items-center text-xs"
                        >
                          {expandedCandidateId === candidate.id ? 'Hide Details' : 'Show Details'}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-3 w-3 ml-1 transition-transform ${
                              expandedCandidateId === candidate.id ? 'transform rotate-180' : ''
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openCvModal(candidate)}
                          className="text-blue-600 hover:text-blue-900 flex items-center text-xs"
                        >
                          View CV
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 ml-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleSingleDownload(candidate.id)}
                          className="text-green-600 hover:text-green-900 flex items-center text-xs"
                        >
                          Download CV
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 ml-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openStatusModal(candidate)}
                          className="text-purple-600 hover:text-purple-900 flex items-center text-xs"
                        >
                          Update Status
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 ml-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openEmailModal(candidate)}
                          disabled={emailLoading || !candidate.email}
                          className="text-orange-600 hover:text-orange-900 flex items-center text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!candidate.email ? "No email address available" : "Send email to candidate"}
                        >
                          {emailLoading ? (
                            <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 ml-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          )}
                          Send Email
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedCandidateId === candidate.id && (
                    <tr>
                      <td colSpan={6 + criteriaNames.length} className="px-3 py-4">
                        <div className="bg-gray-50 p-4 rounded-md">
                          <h3 className="text-lg font-medium text-gray-900 mb-3">Evaluation Details</h3>
                          
                          {/* Notes section */}
                          {candidate.notes && (
                            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                              <h4 className="font-medium text-gray-800 mb-1 text-sm">Notes:</h4>
                              <p className="text-sm text-gray-700">{candidate.notes}</p>
                              {candidate.status_updated_at && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Last updated: {new Date(candidate.status_updated_at).toLocaleString()}
                                </p>
                              )}
                            </div>
                          )}
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {criteriaNames.map((criterion) => (
                              <div key={criterion} className="bg-white p-3 rounded shadow-sm">
                                <h4 className="font-medium text-gray-800 mb-1 capitalize text-sm">
                                  {criterion.replace(/_/g, ' ')}
                                </h4>
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-xs text-gray-500">Score:</span>
                                  <span className={`text-sm font-medium ${
                                    candidate[criterion] >= 0.7 
                                      ? 'text-green-600' 
                                      : candidate[criterion] >= 0.4 
                                        ? 'text-yellow-600' 
                                        : 'text-red-600'
                                  }`}>
                                    {formatScore(candidate[criterion])}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-600 border-t pt-2 mt-2">
                                  <span className="font-medium">Reasoning:</span>{' '}
                                  {candidate.reasoning[criterion]}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {sortedCandidates.length === 0 && (
                <tr>
                  <td colSpan={6 + criteriaNames.length} className="px-3 py-6 text-center text-sm text-gray-500">
                    No candidates have been evaluated yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CV Modal */}
      {showCvModal && selectedCandidate && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeCvModal}
        >
          <div 
            className="bg-white rounded-lg max-w-6xl w-full h-5/6 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {formatDisplayName(selectedCandidate.name)} - CV
                </h2>
                <p className="text-sm text-gray-600">{selectedCandidate.file_name}</p>
              </div>
              <button
                onClick={closeCvModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 p-4">
              <iframe
                src={getCandidateFileUrl(selectedCandidate.id)}
                className="w-full h-full border rounded"
                title={`CV of ${formatDisplayName(selectedCandidate.name)}`}
              />
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end items-center p-4 border-t space-x-3">
              <button
                onClick={() => handleSingleDownload(selectedCandidate.id)}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors flex items-center"
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download CV
              </button>
              <a
                href={getCandidateFileUrl(selectedCandidate.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors flex items-center"
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                Open in New Tab
              </a>
              <button
                onClick={closeCvModal}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && statusModalCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-purple-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Update Candidate Status</h3>
                <p className="text-sm text-gray-500">{formatDisplayName(statusModalCandidate.name)}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Status Selection */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  value={statusForm.status}
                  onChange={(e) => setStatusForm(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {statusOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  value={statusForm.notes}
                  onChange={(e) => setStatusForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any notes about this candidate..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closeStatusModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={updatingStatus}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {updatingStatus ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  'Update Status'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && emailModalCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-5/6 overflow-y-auto">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-orange-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-medium text-gray-900">Send Email</h3>
                <p className="text-sm text-gray-500">{formatDisplayName(emailModalCandidate.name)}</p>
              </div>
              <button
                onClick={closeEmailModal}
                className="text-gray-400 hover:text-gray-600"
                data-testid="email-modal-close"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Email Template */}
              <div>
                <label htmlFor="email_template_type" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Type
                </label>
                <select
                  id="email_template_type"
                  value={emailTemplateType}
                  onChange={handleEmailTemplateChange}
                  disabled={emailTemplateLoading || sendingEmail}
                  data-testid="email-template-select"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                >
                  {EMAIL_TEMPLATE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {emailTemplateLoading && (
                  <p className="mt-1 text-xs text-gray-500" data-testid="email-template-loading">
                    Loading template...
                  </p>
                )}
              </div>

              {/* Recipient Email */}
              <div>
                <label htmlFor="recipient_email" className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email
                </label>
                <input
                  type="email"
                  id="recipient_email"
                  value={emailForm.recipient_email}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                  placeholder="candidate@example.com"
                  disabled={emailTemplateLoading || sendingEmail}
                  data-testid="email-recipient-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Subject */}
              <div>
                <label htmlFor="email_subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  id="email_subject"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject"
                  disabled={emailTemplateLoading || sendingEmail}
                  data-testid="email-subject-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Email Body */}
              <div>
                <label htmlFor="email_body" className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  id="email_body"
                  value={emailForm.body}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Email content..."
                  disabled={emailTemplateLoading || sendingEmail}
                  data-testid="email-body-input"
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  required
                />
              </div>

              {/* Help text */}
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-700">
                  <strong>Tip:</strong> You can edit the email content above before sending. 
                  The template automatically includes candidate details and job information.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closeEmailModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={
                  sendingEmail
                  || emailTemplateLoading
                  || !emailForm.recipient_email
                  || !emailForm.subject
                  || !emailForm.body
                }
                data-testid="email-send-button"
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {sendingEmail ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPage;
